import React from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Chip,
  Avatar
} from '@mui/material';
import { 
  YouTube as YouTubeIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';

interface VideoMetadataCardProps {
  videoId: string;
  title: string;
  duration: string;
  thumbnailUrl: string;
  channelTitle?: string;
  publishedAt?: string;
  transcriptSource?: 'whisperapi' | 'cache';
  transcriptQuality?: number; // 1-10 scale
}

const VideoMetadataCard: React.FC<VideoMetadataCardProps> = ({
  videoId,
  title,
  duration,
  thumbnailUrl,
  channelTitle,
  publishedAt,
  transcriptSource = 'whisperapi',
  transcriptQuality = 8
}) => {
  // Format published date
  const formatPublishedDate = (dateString?: string): string => {
    if (!dateString) return '';
    
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };
  
  // No unused functions
  
  return (
    <Paper 
      elevation={0} 
      className="glass-container"
      sx={{ 
        p: 3,
        mb: 4
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
        {/* Thumbnail */}
        <Box 
          sx={{ 
            width: { xs: '100%', sm: 240 },
            height: { xs: 180, sm: 135 },
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            flexShrink: 0
          }}
        >
          <Box
            component="img"
            src={thumbnailUrl}
            alt={title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover'
            }}
          />
          
          <Box
            sx={{
              position: 'absolute',
              bottom: 8,
              right: 8,
              bgcolor: 'rgba(0, 0, 0, 0.7)',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.75rem'
            }}
          >
            <AccessTimeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            {duration}
          </Box>
          
          <Box
            sx={{
              position: 'absolute',
              top: 8,
              left: 8,
              bgcolor: 'error.main',
              color: 'white',
              px: 1,
              py: 0.5,
              borderRadius: 1,
              display: 'flex',
              alignItems: 'center',
              fontSize: '0.75rem'
            }}
          >
            <YouTubeIcon fontSize="inherit" sx={{ mr: 0.5 }} />
            YouTube
          </Box>
        </Box>
        
        {/* Metadata */}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" gutterBottom>
            {title}
          </Typography>
          
          {channelTitle && (
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Avatar
                sx={{ 
                  width: 24, 
                  height: 24, 
                  mr: 1,
                  bgcolor: 'primary.main'
                }}
              >
                {channelTitle.charAt(0).toUpperCase()}
              </Avatar>
              
              <Typography variant="body2" color="text.secondary">
                {channelTitle}
              </Typography>
              
              {publishedAt && (
                <Typography variant="body2" color="text.secondary" sx={{ ml: 2 }}>
                  {formatPublishedDate(publishedAt)}
                </Typography>
              )}
            </Box>
          )}
          
          {/* Video metadata - removed technical details */}
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 2 }}>
            <Chip
              icon={<AccessTimeIcon />}
              label={`Duration: ${duration}`}
              size="small"
              color="primary"
              variant="outlined"
            />
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

export default VideoMetadataCard;
